import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FilterDropdown from '../common/FilterDropdown';
import ActionButton from '../common/ActionButton';

interface Order {
  id: string;
  order_number: string;
  status: string;
  type: string;
  payment_status: string;
  payment_method?: string;
  total_amount: number;
  created_at: string;
  sitename?: string;
  customer_name?: string;
}

interface OrdersTableProps {
  orders: Order[];
  onReorder?: (orderId: string) => void;
  showAllLink?: boolean;
  compact?: boolean;
  showHeader?: boolean;
  // Inline filters
  statusFilter?: string[];
  paymentStatusFilter?: string[];
  onStatusFilterChange?: (values: string[]) => void;
  onPaymentStatusFilterChange?: (values: string[]) => void;
  availableStatuses?: string[];
  availablePaymentStatuses?: string[];
  showFilters?: boolean;
  // Sorting
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  onReorder,
  showAllLink = false,
  compact = false,
  showHeader = true,
  statusFilter = [],
  paymentStatusFilter = [],
  onStatusFilterChange,
  onPaymentStatusFilterChange,
  availableStatuses = [],
  availablePaymentStatuses = [],
  showFilters = false,
  sortBy = 'created_at',
  sortOrder = 'desc',
  onSort
}) => {
  const navigate = useNavigate();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getStatusColor = (status: string): string => {
    // Red for problem statuses, black for everything else
    const redStatuses = ['cancelled', 'rejected', 'expired', 'failed', 'refunded', 'returned'];
    const statusLower = status.toLowerCase();

    if (redStatuses.includes(statusLower)) {
      return '#dc3545'; // Red
    }
    return '#000000'; // Black
  };

  const getPaymentStatusColor = (status: string): string => {
    // Red for problem statuses, black for everything else
    const redStatuses = ['failed', 'cancelled'];
    const statusLower = status.toLowerCase();

    if (redStatuses.includes(statusLower)) {
      return '#dc3545'; // Red
    }
    return '#000000'; // Black
  };

  // Check if all orders have the same status (hide filter if only one unique value)
  const hasMultipleStatuses = () => {
    const uniqueStatuses = new Set(orders.map(order => order.status));
    return uniqueStatuses.size > 1;
  };

  // Check if all orders have the same payment status (hide filter if only one unique value)
  const hasMultiplePaymentStatuses = () => {
    const uniquePaymentStatuses = new Set(orders.map(order => order.payment_status));
    return uniquePaymentStatuses.size > 1;
  };

  return (
    <div className="card mb-4 border-0 shadow-sm" style={{ borderRadius: '8px', overflow: 'hidden' }}>
      {showHeader && (
        <div className="card-header text-white" style={{ background: '#4D64C1', padding: '15px 20px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ fontSize: '1.3rem', fontWeight: '500' }}>
              <i className="fas fa-box-open me-2"></i>Recent Orders
            </div>
            {showAllLink && (
              <Link to="/orders" className="btn btn-sm btn-outline-light" style={{ borderRadius: '4px' }}>
                View All <i className="fas fa-arrow-right ms-1"></i>
              </Link>
            )}
          </div>
        </div>
      )}
      <div className="card-body p-0">
        <div style={{ overflowX: 'auto', width: '100%', overflowY: 'hidden' }}>
          <table style={{
            borderCollapse: 'separate',
            borderSpacing: '0',
            minWidth: '1202px',
            tableLayout: 'fixed',
            width: '100%',
            marginBottom: '0',
            backgroundColor: 'transparent',
            border: 'none'
          }}>
            <thead style={{
              position: 'sticky',
              top: 0,
              background: '#4D64C1',
              zIndex: 10
            }}>
              <tr>
                <th
                  style={{
                    width: '110px',
                    fontSize: '1.05rem',
                    padding: '12px 8px',
                    fontWeight: '500',
                    color: 'white',
                    borderBottom: 'none',
                    cursor: onSort ? 'pointer' : 'default',
                    userSelect: 'none'
                  }}
                  onClick={() => onSort?.('created_at')}
                >
                  Date
                  {onSort && (
                    <i
                      className={`fas fa-sort-${sortBy === 'created_at' ? (sortOrder === 'asc' ? 'up' : 'down') : 'down'} ms-1`}
                      style={{ fontSize: '0.85rem', opacity: sortBy === 'created_at' ? 1 : 0.5 }}
                    ></i>
                  )}
                </th>
                <th style={{ width: '180px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Order Number
                </th>
                <th style={{ width: '160px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Status
                  {showFilters && onStatusFilterChange && hasMultipleStatuses() && (
                    <FilterDropdown
                      options={availableStatuses}
                      selectedValues={statusFilter}
                      onChange={onStatusFilterChange}
                      label="Status"
                    />
                  )}
                </th>
                <th style={{ width: '160px', fontSize: '1.05rem', padding: '12px 8px 12px 16px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Payment
                  {showFilters && onPaymentStatusFilterChange && hasMultiplePaymentStatuses() && (
                    <FilterDropdown
                      options={availablePaymentStatuses}
                      selectedValues={paymentStatusFilter}
                      onChange={onPaymentStatusFilterChange}
                      label="Payment"
                    />
                  )}
                </th>
                <th style={{ width: '200px', fontSize: '1.05rem', padding: '12px 70px 12px 8px', textAlign: 'right', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Total
                </th>
                <th style={{ width: '452px', fontSize: '1.05rem', padding: '12px 8px', fontWeight: '500', color: 'white', borderBottom: 'none' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: '1.05rem' }}>
              {orders.map((order) => {
                const orderDate = formatDate(order.created_at);

                return (
                  <tr key={order.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem', fontWeight: 'bold' }}>
                      {orderDate}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem' }}>
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-decoration-none fw-bold"
                        style={{
                          color: '#212529'
                        }}
                      >
                        ORD-{order.order_number.slice(-4)}
                      </Link>
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '0.95rem' }}>
                      <span style={{
                        color: getStatusColor(order.status),
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {order.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 8px 10px 16px', verticalAlign: 'middle', fontSize: '1rem' }}>
                      <span style={{
                        color: getPaymentStatusColor(order.payment_status),
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td style={{
                      padding: '10px 70px 10px 8px',
                      verticalAlign: 'middle',
                      fontSize: '1rem',
                      textAlign: 'right',
                      fontWeight: 'bold'
                    }}>
                      {formatCurrency(order.total_amount)}
                    </td>
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', fontSize: '1rem', minWidth: '452px' }}>
                      <div className="d-flex gap-1" style={{ justifyContent: 'flex-start' }}>
                        <ActionButton
                          variant="primary"
                          onClick={() => navigate(`/orders/${order.id}`)}
                          title="View Order"
                        >
                          View
                        </ActionButton>
                        {onReorder && (
                          <ActionButton
                            variant="success"
                            onClick={() => onReorder(order.id)}
                            title="Reorder"
                          >
                            Reorder
                          </ActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center" style={{ padding: '20px', color: '#6c757d' }}>
                    <i className="fas fa-info-circle me-2"></i> No orders found.
                    <Link to="/products" className="ms-2">Start shopping</Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;